'use client';

import { useEffect, useRef } from 'react';

/**
 * 首屏背景：多条辉光正弦波沿水平方向缓慢流动，随鼠标位置产生局部起伏。
 *
 * 取代早先的 HeroShader / BlinkingSquares：同样只渲染一张铺满父容器的 canvas，作为
 * 绝对定位的背景层（className 由父级给定，含底部 mask 渐隐）。固定使用亮色配色，
 * 贴合 vidu-s 蓝色调，而非组件原始 demo 的主题色。
 *
 * 从给定的 GlowyWavesHero 里只抽取背景画布部分：demo 的营销文案、stats、按钮、
 * framer-motion 依赖都不要，落地页首屏的标题/按钮由 ViduS 自己叠在上层。
 *
 * prefers-reduced-motion 下减弱鼠标影响并放慢。
 */

type Point = { x: number; y: number };

interface WaveConfig {
  offset: number;
  amplitude: number;
  frequency: number;
  color: string;
  opacity: number;
}

/** 底色渐变（自上而下的色标）+ 波形调色板 + ASCII 字符色。 */
type Palette = {
  bgStops: { offset: number; color: string }[];
  waveRgb: [number, number, number][];
  asciiRgb: [number, number, number];
};

const PALETTE: Palette = {
  // 柔和蓝白渐变。
  bgStops: [
    { offset: 0, color: '#c5d0f6' },
    { offset: 0.4, color: '#eef1f6' },
    { offset: 1, color: '#f5f6f8' },
  ],
  // 浅底上要压得住：整体加深，避免波形消失在浅底里。
  waveRgb: [
    [79, 108, 196],
    [125, 151, 221],
    [74, 95, 168],
    [149, 170, 224],
    [95, 118, 190],
  ],
  asciiRgb: [70, 92, 164],
};

/** 各波的形状参数（相位/振幅/频率/不透明度），配色单独按主题取。 */
const WAVE_SHAPE: Omit<WaveConfig, 'color'>[] = [
  { offset: 0, amplitude: 70, frequency: 0.003, opacity: 0.45 },
  { offset: Math.PI / 2, amplitude: 90, frequency: 0.0026, opacity: 0.35 },
  { offset: Math.PI, amplitude: 60, frequency: 0.0034, opacity: 0.3 },
  { offset: Math.PI * 1.5, amplitude: 80, frequency: 0.0022, opacity: 0.25 },
  { offset: Math.PI * 2, amplitude: 55, frequency: 0.004, opacity: 0.2 },
];

/** 波形基线占画面高度的比例：0.5 是正中，调大让整簇线往下沉。 */
const BASELINE = 0.62;

/** 整张背景渐变的整体不透明度（波形线不受影响，另按各自 opacity 画）。 */
const BG_ALPHA = 0.48;

/** 整层波形线（含各自 opacity 与辉光）合成到主画布时的整体不透明度。 */
const WAVE_LAYER_ALPHA = 0.9;

/** 呼吸感 ASCII 层：字符位置固定不动，每格独立呼吸（各自相位+频率），靠底边渐隐。 */
const ASCII_RAMP = '·:+*o=#';
const ASCII_CELL = 22; // 字符网格间距(px)
const ASCII_FONT = 12;
const ASCII_ALPHA = 0.16; // 峰值透明度，压得很淡
const ASCII_KEEP = 0.34; // 平均保留比例；实际疏密再叠一层低频扰动，各区块疏密不一
const ASCII_FADE_BAND = 0.22; // 底部这一比例高度内线性渐隐到 0

/** 每格一个稳定伪随机（0~1）。用不同种子取多个互相独立的随机量，避免字符/疏密/相位相关联而聚成团。 */
const cellHash = (gx: number, gy: number, seed: number) => {
  const n = Math.sin((gx + 1) * 127.1 + (gy + 1) * 311.7 + seed * 74.7) * 43758.5453;
  return n - Math.floor(n);
};

export interface HeroShaderProps {
  className?: string;
}

export const HeroShader = ({ className }: HeroShaderProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef<Point>({ x: 0, y: 0 });
  const targetMouseRef = useRef<Point>({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mouseInfluence = reduced ? 10 : 70;
    const influenceRadius = reduced ? 160 : 320;
    const smoothing = reduced ? 0.04 : 0.1;

    let dpr = 1;
    let width = 0;
    let height = 0;

    const resize = () => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      // setTransform 而非 scale：scale 累乘，第二次 resize 会叠加 DPR。
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const recenter = () => {
      const center = { x: width / 2, y: height / 2 };
      mouseRef.current = center;
      targetMouseRef.current = center;
    };
    recenter();

    const ro = new ResizeObserver(() => {
      resize();
      recenter();
    });
    ro.observe(canvas);

    // 鼠标坐标转成 canvas 内的局部坐标（背景层 pointer-events:none，监听挂 window）。
    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      targetMouseRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    const handleMouseLeave = () => recenter();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    let time = 0;
    let raf = 0;

    // 单条波在横坐标 x 处的 y。
    const waveY = (x: number, shape: Omit<WaveConfig, 'color'>) => {
      const baseY = height * BASELINE;
      const dx = x - mouseRef.current.x;
      const dy = baseY - mouseRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const influence = Math.max(0, 1 - distance / influenceRadius);
      const mouseEffect = influence * mouseInfluence * Math.sin(time * 0.001 + x * 0.01 + shape.offset);
      return (
        baseY +
        Math.sin(x * shape.frequency + time * 0.002 + shape.offset) * shape.amplitude +
        Math.sin(x * shape.frequency * 0.4 + time * 0.003) * (shape.amplitude * 0.45) +
        mouseEffect
      );
    };

    // 波形线画到离屏层，整层再以 WAVE_LAYER_ALPHA 合成回主画布（各线相对深浅保留）。
    const waveCanvas = document.createElement('canvas');
    const waveCtx = waveCanvas.getContext('2d');

    const drawWave = (
      target: CanvasRenderingContext2D,
      shape: Omit<WaveConfig, 'color'>,
      rgb: [number, number, number],
    ) => {
      target.save();
      target.beginPath();
      for (let x = 0; x <= width; x += 4) {
        const y = waveY(x, shape);
        if (x === 0) target.moveTo(x, y);
        else target.lineTo(x, y);
      }
      const color = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
      target.lineWidth = 2.5;
      target.strokeStyle = color;
      target.globalAlpha = shape.opacity;
      target.shadowBlur = 35;
      target.shadowColor = color;
      target.stroke();
      target.restore();
    };

    // 呼吸感 ASCII：字符位置固定，每格「个体呼吸」—— 各自的相位与频率都不同，
    // 于是每个字符独立地一起一伏，而非整片同步；越靠底边越淡（线性渐隐到 0）；
    // 疏密与分布都随机（保留概率叠一层低频空间扰动，各区块疏密不一、不聚成团）。
    const drawAscii = (rgb: [number, number, number]) => {
      const cols = Math.ceil(width / ASCII_CELL);
      const rows = Math.ceil(height / ASCII_CELL);
      const rampMax = ASCII_RAMP.length - 1;
      const fadeStart = rows * (1 - ASCII_FADE_BAND);
      ctx.save();
      ctx.font = `${ASCII_FONT}px monospace`;
      ctx.textBaseline = 'top';
      ctx.fillStyle = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
      ctx.shadowBlur = 0;
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          // 疏密随机：本格保留概率 = 基准 + 低频空间扰动（相邻区块整体偏密/偏疏）。
          const region = cellHash(Math.floor(gx / 5), Math.floor(gy / 5), 9);
          const keep = ASCII_KEEP + (region - 0.5) * 0.5;
          if (cellHash(gx, gy, 1) > keep) continue; // 落选留白，分布随机
          // 底部渐隐：进入底边条带后线性压到 0。
          let fade = 1;
          if (gy >= fadeStart) fade = Math.max(0, 1 - (gy - fadeStart) / (rows - fadeStart));
          if (fade <= 0) continue;
          // 个体呼吸：相位、频率各格独立，互不同步。
          const phase = cellHash(gx, gy, 2) * Math.PI * 2;
          const speed = 0.022 + cellHash(gx, gy, 3) * 0.055;
          const breath = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(time * speed + phase));
          const alpha = ASCII_ALPHA * breath * fade;
          if (alpha < 0.01) continue;
          const ch = ASCII_RAMP[Math.min(rampMax, Math.floor(cellHash(gx, gy, 4) * (rampMax + 1)))];
          ctx.globalAlpha = alpha;
          ctx.fillText(ch, gx * ASCII_CELL, gy * ASCII_CELL);
        }
      }
      ctx.restore();
    };

    const animate = () => {
      if (!reduced) time += 1;

      mouseRef.current.x += (targetMouseRef.current.x - mouseRef.current.x) * smoothing;
      mouseRef.current.y += (targetMouseRef.current.y - mouseRef.current.y) * smoothing;

      // 每帧先清空：底色渐变以 64% 不透明度重铺，故必须先清，否则半透明层会逐帧累积成实色。
      ctx.clearRect(0, 0, width, height);
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      for (const stop of PALETTE.bgStops) {
        gradient.addColorStop(stop.offset, stop.color);
      }
      ctx.globalAlpha = BG_ALPHA;
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // 呼吸 ASCII 垫在辉光线下面。
      drawAscii(PALETTE.asciiRgb);

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      if (waveCtx) {
        // 离屏层每帧重画：尺寸对齐主画布（含 DPR），坐标系同样按 DPR 缩放。
        if (waveCanvas.width !== canvas.width || waveCanvas.height !== canvas.height) {
          waveCanvas.width = canvas.width;
          waveCanvas.height = canvas.height;
        }
        waveCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        waveCtx.clearRect(0, 0, width, height);
        for (let i = 0; i < WAVE_SHAPE.length; i++) {
          drawWave(waveCtx, WAVE_SHAPE[i], PALETTE.waveRgb[i]);
        }
        // 整层以 WAVE_LAYER_ALPHA 合成，各线相对深浅保留。
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = WAVE_LAYER_ALPHA;
        ctx.drawImage(waveCanvas, 0, 0);
        ctx.restore();
      }

      raf = window.requestAnimationFrame(animate);
    };
    raf = window.requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} />;
};
